// React
import { FC, Ref, useMemo } from 'react';

// Custom components
import UserAvatar from 'components/Avatar';
import ItemDateTime from 'components/ChecklistReport/ItemDateTime';

// Utilities
import { statusDropdownData } from 'helper/task/dropdownData';
import DateTime from 'utils/DateTime';
import { getTimeZone } from 'utils/globalFunction';

// Icons
import GlobeIcon from 'components/Icons/globeIcon';
import LogoXenia from 'public/assets/img/logo.png';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';

// Styled
import {
  Wrapper,
  OutlinedBox,
  OrganisationMeta,
  OrganisationName,
  ReportIconAndName,
  TemplateName,
  ReportGeneratedAndLogo,
  ReportGeneratedBy,
  GreyBox,
  GreyRow,
  RowText,
  RowDetailTextWrapper,
  Percentage,
  AnonymousAvatar,
  CheckLisLogIconCnt,
  RowLink,
  StatusChip,
  LocationAndAddress,
} from './styled';

// Hooks
import { useCustomPermission } from 'utils/CustomHooks/useCustomPermission';
import { getLogDuration, getOverallPercentage } from 'utils/templates';
import ApprovalStatus from 'components/ApprovalStatus';
import { Box } from '@mui/material';
import useLocationsMap from 'utils/CustomHooks/useLocationsMap';
import { scoreSummaryStyles } from '../scoreSummary/styles';
import { getMatchingScoreConfig } from 'utils/templates/scoringConfig';
import moment from 'moment-timezone';
import useDateTime from 'utils/CustomHooks/useDateTime';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import CustomToolTip from 'components/Tooltip';

interface IProps {
  checklistLog: any;
  correctiveTaskLogItems: any;
  hotelName: string;
  reference: Ref<any>;
  correctiveTaskCount: number;
  flaggedItemCount: number;
  isPublic: boolean;
  template?: any;
  scrollTo: (id: string) => void;
}
const DEFAULT_COLOR = '#1f1e1ee0';
const ChecklistLogReportOverview: FC<IProps> = (props) => {
  const {
    checklistLog,
    correctiveTaskLogItems,
    hotelName,
    reference,
    correctiveTaskCount,
    flaggedItemCount,
    isPublic,
    scrollTo,
    template,
  } = props;
  const { labelTimezone } = useDateTime();
  const { hasFeature, FEATURES } = useCustomPermission();
  const { navigateWithWorkspaceUrl } = useWorkspaceHook();

  // Submitter Name
  const getSubmitterName = (log: any) => {
    if (log.submitterName) return log.submitterName;
    if (!log.LastItemUpdater || !log.Updater) return 'Anonymous';
    else {
      const updater = log.LastItemUpdater ?? log.Updater;
      return `${updater?.firstName} ${updater?.lastName}`;
    }
  };

  const percentage =
    checklistLog?.progress?.passFailCompletedCount &&
    !checklistLog?.progress?.passFailCount
      ? 'N/A'
      : `${checklistLog?.progress?.progress}%`;

  const completion = `(${checklistLog?.progress?.completedCount} out of ${checklistLog?.progress?.count} items)`;

  const duration = getLogDuration(checklistLog);

  const approvalStatus = useMemo(() => {
    return checklistLog?.ApprovalSteps?.[0]?.status;
  }, [checklistLog]);
  const groupTasksByStatus = (logs) => {
    // First, extract all tasks from the items
    const allTasks = logs.flatMap((item) => item.Tasks || []);

    // Group tasks by status
    const tasksByStatus = allTasks.reduce((acc, task) => {
      const status = task.taskStatus;
      if (!acc[status]) {
        acc[status] = 0;
      }
      acc[status]++;
      return acc;
    }, {});

    // Convert to array format if needed
    const result = Object.entries(tasksByStatus).map(([status, count]) => ({
      status,
      count,
    }));

    return result;
  };

  // Example usage
  const taskStatusCounts = useMemo(() => {
    return groupTasksByStatus(correctiveTaskLogItems);
  }, [correctiveTaskLogItems]);

  const TaskStatusChip = ({ status, count }) => {
    const statusData = statusDropdownData.find((item) => item.value === status);
    return (
      <StatusChip color={statusData?.color}>
        <span className="dot"></span>
        {status} <b>({count})</b>
      </StatusChip>
    );
  };

  const locationGroupHash = useLocationsMap(true);

  const matchedConfig = getMatchingScoreConfig(
    checklistLog?.score?.percentage,
    template?.HotelChecklist?.scoreConfig,
  );

  const tz = checklistLog?.Updater?.timezone || getTimeZone();

  const offset = moment.tz(tz).format('Z'); // returns offset like +01:00 or +00:00
  const gmtFormat = `GMT ${offset}`;

  const overallPercentage = getOverallPercentage(checklistLog?.score);

  return (
    <Wrapper ref={reference} id="overview">
      <OutlinedBox>
        <ReportGeneratedAndLogo>
          <img
            src={
              isPublic
                ? checklistLog?.Checklist.logo || LogoXenia
                : hasFeature(FEATURES.TEMPLATE_REPORT_LOGO)
                ? checklistLog?.Checklist.logo || LogoXenia
                : LogoXenia
            }
            className="xenia-logo"
            {...((!isPublic && !hasFeature(FEATURES.TEMPLATE_REPORT_LOGO)) ||
            !checklistLog?.Checklist.logo
              ? { width: 130 }
              : { style: { maxWidth: '200px' } })}
          />
        </ReportGeneratedAndLogo>
        <OrganisationMeta>
          <OrganisationName>{hotelName}</OrganisationName>
          <ReportIconAndName>
            <TemplateName>{checklistLog?.name} Report</TemplateName>
          </ReportIconAndName>
        </OrganisationMeta>
      </OutlinedBox>
      <LocationAndAddress>
        <div className="left">
          <div className="location">
            {checklistLog?.DefaultLocation?.name ||
              locationGroupHash[checklistLog?.DefaultLocation?.id]?.name}
          </div>
          <div className="address">
            {checklistLog?.DefaultLocation?.address ||
            locationGroupHash[checklistLog?.DefaultLocation?.id]?.address
              ? `${
                  checklistLog?.DefaultLocation?.address ||
                  locationGroupHash[checklistLog?.DefaultLocation?.id]?.address
                }`
              : ''}
          </div>
        </div>
        <div className="right">
          <div className="date">
            {moment
              .tz(checklistLog.updatedAt, tz)
              .format('MMM DD, YYYY [at] h:mm A')}
          </div>
          <div className="timezone">{`${labelTimezone(
            tz,
          )} (${gmtFormat})`}</div>
        </div>
      </LocationAndAddress>
      <GreyBox>
        <GreyRow>
          <RowText type="title">Completion:</RowText>
          <RowDetailTextWrapper>
            <Percentage component="span">{percentage}</Percentage>
            <RowText>{completion}</RowText>
          </RowDetailTextWrapper>
        </GreyRow>
        {checklistLog?.Checklist?.isScoring && (
          <GreyRow>
            <RowText type="title">Weighted Score:</RowText>
            <RowDetailTextWrapper>
              <Box
                component="span"
                sx={{
                  ...scoreSummaryStyles.overallScoreChip(overallPercentage),
                  backgroundColor: `${
                    matchedConfig?.color ?? DEFAULT_COLOR
                  } !important`,
                  color: 'white !important',
                }}
              >
                <Percentage component="span" sx={{ color: 'white' }}>
                  {matchedConfig?.rating ?? 'Score'} • {overallPercentage}%
                </Percentage>
                <RowText sx={{ color: 'white' }}>
                  ({checklistLog.score.earned} out of {checklistLog.score.total}{' '}
                  pts)
                </RowText>
              </Box>
            </RowDetailTextWrapper>
          </GreyRow>
        )}
        {duration && (
          <GreyRow>
            <RowText type="title">Duration:</RowText>
            <RowDetailTextWrapper>
              <RowText type="title">{duration}</RowText>
            </RowDetailTextWrapper>
          </GreyRow>
        )}
        <GreyRow>
          <RowText type="title">Corrective Tasks:</RowText>
          <RowDetailTextWrapper>
            <Box textAlign={'right'}>
              <RowLink
                mb={1}
                type={'link'}
                onClick={() => {
                  scrollTo('correctiveTasks');
                }}
              >
                {correctiveTaskCount}
              </RowLink>
              <RowDetailTextWrapper>
                {taskStatusCounts.map((item) => {
                  return (
                    <TaskStatusChip status={item.status} count={item.count} />
                  );
                })}
              </RowDetailTextWrapper>
            </Box>
          </RowDetailTextWrapper>
        </GreyRow>
        <GreyRow>
          <RowText type="title">Flagged Items:</RowText>
          <RowDetailTextWrapper>
            <RowText>{flaggedItemCount}</RowText>
          </RowDetailTextWrapper>
        </GreyRow>
        <GreyRow>
          <RowText type="title">Date/Time:</RowText>
          <ItemDateTime itemDateTime={checklistLog.updatedAt} />
        </GreyRow>
        {checklistLog?.editedAt && (
          <GreyRow>
            <RowText type="title">Last Edited:</RowText>
            <RowLink
              type={'link'}
              onClick={() => {
                navigateWithWorkspaceUrl(
                  `/submission/${checklistLog?.id}/history`,
                );
              }}
            >
              <CustomToolTip
                title="View submission history"
                placement="top"
                arrow={true}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <UpdateOutlinedIcon sx={{ fontSize: '18px' }} />
                  <ItemDateTime
                    itemDateTime={checklistLog.editedAt}
                    sx={{ color: 'inherit' }}
                  />
                </Box>
              </CustomToolTip>
            </RowLink>
          </GreyRow>
        )}
        {approvalStatus && (
          <GreyRow>
            <RowText type="title">Approval Status:</RowText>
            <ApprovalStatus status={approvalStatus} />
          </GreyRow>
        )}

        <GreyRow noborder="true">
          <RowText type="title">Submitted by:</RowText>
          <RowDetailTextWrapper>
            {checklistLog?.LastItemUpdater ? (
              <UserAvatar
                width={20}
                height={20}
                profilePic={checklistLog?.LastItemUpdater?.photo}
                userId={checklistLog?.LastItemUpdater?.id}
                firstName={checklistLog?.LastItemUpdater?.firstName}
              />
            ) : (
              <AnonymousAvatar>
                <GlobeIcon style={{ color: '#fff', fontSize: '16px' }} />
              </AnonymousAvatar>
            )}

            {checklistLog?.LastItemUpdater && (
              <RowText>{`${checklistLog?.LastItemUpdater?.firstName || ''}
                  ${checklistLog?.LastItemUpdater?.lastName || ''}`}</RowText>
            )}
            {!checklistLog?.LastItemUpdater && (
              <RowText>{getSubmitterName(checklistLog)}</RowText>
            )}
          </RowDetailTextWrapper>
        </GreyRow>
      </GreyBox>
    </Wrapper>
  );
};

export default ChecklistLogReportOverview;
